import React, { useState, useEffect, useRef } from 'react';
import Helmet from 'react-helmet';
import { navigate } from 'gatsby';
import { useCookies, CookiesProvider } from 'react-cookie';
import md5 from 'md5';

import { GlobalWrapper } from '../../uiComponents';
import { Header } from '../../components/header/header.component';
import { Footer } from '../../components/footer/footer.component';
import { AlertPopup } from '../../components/alertPopup/alertPopup.component';
import { CookiePopup } from '../../components/cookiePopup/cookiePopup.component';
import { AlertBar } from '../../components/alertBar/alertBar.component';
import { FullscreenMenu } from '../../components/fullscreenMenu/fullscreenMenu.component';
import { MenuData } from '../../interfaces/menu.types';
import { LanguageSlug } from '../../enums/languageSlug.enum';
import { GlobalStyle } from '../../theme/global.styled';
import { CustomStyles } from '../../theme/customStyles.styled';
import { AlertSettings } from '../../interfaces/alertsSettings.types';
import { hexToRgba, renderExternalLink, renderInternalLink, renderWhenTrue, renderWhenTrueOr } from '../../utils';
import { Color } from '../../theme';

import { PrimaryLayoutData } from './primary.types';
import { MiniPopupsContainer } from './primary.styled';
import { PageType } from '../../enums/pageType.enum';
import { BasePath } from '../../interfaces/basePath.types';

export const PrimaryLayout: React.FC<PrimaryLayoutData> = (data: PrimaryLayoutData) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return <div />;
  }

  const {
    children,
    seoData,
    languages,
    menuData,
    settings,
    availableTranslations,
    logoData,
    alertsSettings,
    searchSettings,
    adBannerDataKey,
    returnToMainBannerDataKey,
    comparisonFullLabelKey,
    hasEuLogosInFooter,
    hasTextContentInAlertBar,
    hasLogoInBanner,
    pathname,
    basePaths,
    project,
  } = data;
  
  const getLanguageFromUrl = () => {
    const url = pathname.split('/');
    let langCode = ''
    switch (url[1]) {
      case LanguageSlug.De:
      case LanguageSlug.En:
      case LanguageSlug.Ru:
        langCode = url[1];
        break;
      default:
        langCode = LanguageSlug.Pl;
        break;
    }

    return currentLanguage !== langCode ? langCode : currentLanguage;
  };

  const justMounted = useRef(true)
  const pageContext = (data.children as any)?.props.pageContext;
  const [isExpandedMenuShown, setIsExpandedMenuShown] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(getLanguageFromUrl());
  const [cookies, setCookie, removeCookie] = useCookies([]);
  const [isAlertPopupVisible, setIsAlertPopupVisible] = useState(false);
  const [isCookiePopupVisible, setIsCookiePopupVisible] = useState(false);
  const [isComparisonAlertBarVisible, setIsComparisonAlertBarVisible] = useState(true);
  const [localComparisonData, setLocalComparisonData] = useState(JSON.parse(localStorage.getItem('comparisonData')) || []);

  const currentMenu = menuData.find((menu: MenuData) => menu.language.slug === currentLanguage);
  const currentAlertSettings = alertsSettings.find((alertSettings: AlertSettings) => alertSettings.language.slug === currentLanguage);
  const { expandedMenu, footerMenu, headerMenu, authorLabel, slider } = currentMenu.acf;

  useEffect(() => {
    if (justMounted.current) {
      justMounted.current = false;
      return;
    }

    if (!justMounted.current) {
      let pagePath = null;
      if (currentLanguage === pageContext.currentLanguage) { return; }
      if (pageContext.data.template === 'homepage') {
        pagePath = currentLanguage === LanguageSlug.Pl ? '/' : `${currentLanguage}/`;
      } else {
        let slug = availableTranslations.find((path) => path.slug === currentLanguage)?.content?.post_name;
        slug = slug === undefined ? '' : slug;
        if (pageContext.isSingleItemTemplate) {
          const paths = basePaths[0]?.project
            ? basePaths.filter((path) => project.toLowerCase() === path.project.toLowerCase()) 
            : basePaths;
            
          const basePath = paths.find((path) => path.language === currentLanguage);
          slug = `${basePath.slug}/${slug}`;
        }  
        pagePath = `${currentLanguage === LanguageSlug.Pl ? '' : currentLanguage + '/'}${slug}`;
      }
      if (pagePath) {
        pagePath = pagePath.indexOf('/') === 0 ? pagePath : `/${pagePath}`;
        navigate(pagePath);
      }
      const comparisonDataLangKey = localStorage.getItem('comparisonDataLangKey');
      if (comparisonDataLangKey && comparisonDataLangKey !== currentLanguage) {
        setLocalComparisonData([]);
        localStorage.removeItem('comparisonData');
        localStorage.removeItem('comparisonDataLangKey');
      }
    }
  }, [currentLanguage]);

  const checkAlerts = () => {
    checkAlert(adBannerDataKey, setIsAlertPopupVisible);
    checkAlert('cookiePopupSettings', setIsCookiePopupVisible);
  };

  const checkAlert = (alertKey: string, setFunction: Function) => {
    if (!currentAlertSettings[alertKey]) { return; }

    const cookieName = getCookieName(currentAlertSettings[alertKey].content);
    const isActive = alertKey === 'cookiePopupSettings' ? true : currentAlertSettings[alertKey].isActive;

    if (!isActive && cookies[cookieName]) {
      removeCookie(cookieName);
    }
    if (isActive && !cookies[cookieName]) {
      setFunction(true);
    }
  };

  useEffect(() => {
    checkAlerts();
  }, [cookies]);

  const getCookieName = (content: string): string => md5(content) as string;

  const closeAlertHandler = (cookieName: string, setFunction: Function) => {
    const expireDate = new Date();
    expireDate.setTime(expireDate.getTime() + 3600 * 1000 * 24 * 14);
    setCookie(cookieName, 'opened', { path: '/', expires: expireDate, secure: true });
    setFunction(false);
  }

  const handleAlertPopupClose = () => closeAlertHandler(
    getCookieName(currentAlertSettings[adBannerDataKey].content),
    setIsAlertPopupVisible
  );

  const handleCookiePopupClose = () => closeAlertHandler(
    getCookieName(currentAlertSettings.cookiePopupSettings.content),
    setIsCookiePopupVisible
  );

  const handleComparisonAlertBarClose = () => setIsComparisonAlertBarVisible(false);
  
  const renderAlertPopup = renderWhenTrue(() => (
    <AlertPopup
      content={currentAlertSettings[adBannerDataKey]?.content}
      isAlertVisible={isAlertPopupVisible}
      closeAction={handleAlertPopupClose}
    />
  ));

  const renderAlertBar = () => {
    const { image } = currentAlertSettings.imagePopupSettings;
    return (
      <AlertBar
        alertContent={currentAlertSettings[returnToMainBannerDataKey].content}
        hasTextContent={hasTextContentInAlertBar}
        logo={image?.localFile?.url}
        hasLogoInBanner={hasLogoInBanner}
      />
    );
  };

  const renderComparisonAlertBar = renderWhenTrue(() => (
    <AlertPopup
      content={currentAlertSettings[comparisonFullLabelKey]}
      isAlertVisible={localComparisonData.length === 3 && isComparisonAlertBarVisible}
      backgroundColor={hexToRgba(Color.gold, 0.7)}
      closeAction={handleComparisonAlertBarClose}
    />
  ));

  document.addEventListener('storage:update', () => {
    const comparisonData = JSON.parse(localStorage.getItem('comparisonData')) || [];
    setLocalComparisonData(comparisonData);
  });

  return (
    <GlobalWrapper>
      <CookiesProvider>
      <GlobalStyle />
      <CustomStyles />
      <Helmet
        title={seoData.title}
        meta={[
          { name: 'description', content: seoData.metaDescription },
          { name: 'og:title', content: seoData.title },
          { name: 'og:description', content: seoData.metaDescription },
          { name: 'og:type', content: 'website' },
        ]}
      >
        <link href='https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;700&display=swap' rel='stylesheet' />
        <link rel='icon' href={seoData.favicon} />
      </Helmet>
      {renderAlertBar()}
      <MiniPopupsContainer>
        {renderComparisonAlertBar(!!comparisonFullLabelKey)}
        {renderAlertPopup(!!adBannerDataKey)}
        <CookiePopup
          content={currentAlertSettings.cookiePopupSettings.content}
          privacyPolicy={currentAlertSettings.cookiePopupSettings.privacyPolicy}
          isAlertVisible={isCookiePopupVisible}
          currentLanguage={currentLanguage}
          readMoreButtonLabel={currentAlertSettings.cookiePopupSettings.readMoreButtonLabel}
          closeAction={handleCookiePopupClose}
        />
      </MiniPopupsContainer>
      <Header
        languages={languages}
        currentLanguage={currentLanguage}
        setCurrentLanguage={setCurrentLanguage}
        menu={headerMenu}
        logoData={logoData}
        showExpandedMenu={setIsExpandedMenuShown}
      />
      {children}
      <Footer
        menu={footerMenu}
        logoData={logoData}
        socialMediaData={settings.socialMedia}
        authorLabel={authorLabel}
        logoGroupData={settings.footerLogoGroup}
        currentLanguage={currentLanguage}
        hasEuLogos={hasEuLogosInFooter}
      />
      <FullscreenMenu
        menuData={expandedMenu}
        sliderData={slider}
        isMenuShown={isExpandedMenuShown}
        socialMediaData={settings.socialMedia}
        hideExpandedMenu={setIsExpandedMenuShown}
        currentLanguage={currentLanguage}
        alertContent={currentAlertSettings[returnToMainBannerDataKey]?.content}
        logoData={logoData}
        languages={languages}
        setCurrentLanguage={setCurrentLanguage}
        searchSettings={searchSettings}
        alertBarLogo={currentAlertSettings.imagePopupSettings.image?.localFile?.url}
        hasLogoInAlertBar={hasLogoInBanner}
      />
      </CookiesProvider>
    </GlobalWrapper>
  );
}
